import { Badge, Button } from 'reactstrap';
import { ColumnDef } from '@tanstack/react-table';
import TableContainer from '../../Campaign/components/TableContainer';
import { NoDataCard } from '~/containers/Campaign/components/NoDataCard';
import { renderIcon } from '~/shared/components/icons';
import { fundRequestListing } from '~/types/fundrequests';
import { useHistory } from 'react-router-dom';
import { toUSD, getBadgeLabel, getBadgeClass } from '~/shared/utils';
import moment from 'moment';

type ExpensesTableProps = {
    fundRequests: fundRequestListing[];
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    isLoading: boolean;
    setDeletingFundRequest: (fundRequest: fundRequestListing) => void;
    onSort?: (columnId: string, direction: 'asc' | 'desc') => void;
    sortBy?: string;
    sortOrder?: string;
};

const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
        case 'needs_info':
            return '#9c3353';
        case 'for_dof_approval':
            return '#f78154';
        case 'submitted':
            return '#25b7b5';
        case 'ap_review':
            return '#febf3e';
        case 'approved':
            return '#92c353';
        case 'paid':
            return '#1e1e1e';
        case 'resubmitted':
            return '#7cd5f3';
        case 'rejected':
            return '#f02d3a';
        default:
            return '#e0e0e0';
    }
};

const getButtonColor = (status: string): string => {
    switch (status.toLowerCase()) {
        case 'submitted':
        case 'in_mip':
            return 'text-primary';
        case 'needs_info':
            return 'text-primary';
        case 'draft':
            return 'text-primary';
        default:
            return 'opacity-25';
    }
};

function ExpensesTable({
    fundRequests,
    currentPage,
    totalPages,
    onPageChange,
    pageSize,
    setPageSize,
    isLoading,
    setDeletingFundRequest,
    onSort,
    sortBy,
    sortOrder,
}: ExpensesTableProps) {
    const history = useHistory();

    const columns: ColumnDef<fundRequestListing>[] = [
        {
            id: 'id',
            header: 'ID',
            accessorKey: 'id',
            enableSorting: true,
            size: 80,
            minSize: 80,
            maxSize: 80,
            accessorFn: (props) => {
                return (
                    <div>
                        <div
                            className='text-primary fw-bold'
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                history.push(
                                    `/requests/request/submit/${props.id}`
                                );
                            }}
                        >
                            {props.id}
                        </div>
                    </div>
                );
            },
        },
        {
            id: 'payee',
            header: 'Payee',
            accessorKey: 'payee',
            enableSorting: true,
            size: 200,
            minSize: 150,
            maxSize: 250,
        },
        {
            id: 'fund_request_status',
            header: 'Status',
            accessorKey: 'fund_request_status',
            enableSorting: true,
            size: 140,
            minSize: 120,
            maxSize: 160,
            accessorFn: (props) => {
                const statusValue = props?.fund_request_status.value;
                const statusLabel = props?.fund_request_status.label;
                const badgeLabel = getBadgeLabel(statusValue, statusLabel);

                // Use the utility function to get consistent badge class
                const badgeClass = getBadgeClass(badgeLabel, statusValue);

                return (
                    <Badge
                        color='none'
                        className={badgeClass}
                        // style={{
                        //     backgroundColor: `${getStatusColor(
                        //         props?.fund_request_status.value
                        //     )}`,
                        // }}
                    >
                        {badgeLabel}
                    </Badge>
                );
            },
        },
        {
            id: 'updated_date',
            header: 'Last Updated',
            accessorKey: 'updated_date',
            enableSorting: true,
            size: 130,
            minSize: 120,
            maxSize: 140,
            accessorFn: (props) => {
                return (
                    <div>{moment(props.updated_date).format('MM/DD/YYYY')}</div>
                );
            },
        },
        {
            id: 'submitted_date',
            header: 'Submitted',
            accessorKey: 'submitted_date',
            enableSorting: true,
            size: 130,
            minSize: 120,
            maxSize: 140,
            accessorFn: (props) => {
                return (
                    <div>
                        {props.submitted_date
                            ? moment(props.submitted_date).format('MM/DD/YYYY')
                            : ''}
                    </div>
                );
            },
        },
        {
            id: 'item_count',
            header: 'Items',
            accessorKey: 'item_count',
            enableSorting: true,
            size: 80,
            minSize: 70,
            maxSize: 90,
        },
        {
            id: 'unread_notes',
            header: 'Unread Notes',
            accessorKey: 'unread_notes',
            enableSorting: false,
            size: 120,
            minSize: 100,
            maxSize: 140,
            accessorFn: (props) => {
                const count = props.unread_notes || 0;
                return (
                    <div className="text-center">
                        <span className={count > 0 ? 'text-danger fw-bold' : ''}>
                            {count > 0 ? count : ''}
                        </span>
                    </div>
                );
            },
        },
        {
            id: 'request_amount',
            header: 'Amount',
            accessorKey: 'request_amount',
            enableSorting: true,
            size: 120,
            minSize: 100,
            maxSize: 140,
            accessorFn: (props) => {
                return <div>{toUSD(props.request_amount)}</div>;
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            size: 120,
            minSize: 100,
            maxSize: 140,
            accessorFn: (props) => {
                const disabledEditStatuses = [
                    'open',
                    'approved',
                    'paid',
                    'rejected',
                    'resubmitted',
                    'in_review',
                    'for_dof_approval',
                    'submitted',
                ];

                return (
                    <div className='d-lg-flex'>
                        <Button
                            color='white'
                            className={`p-1 m-0 ${
                                disabledEditStatuses.includes(
                                    props.fund_request_status.value
                                )
                                    ? 'opacity-25'
                                    : getButtonColor(
                                          props.fund_request_status.value
                                      )
                            }`}
                            style={{ fontSize: '20px' }}
                            disabled={disabledEditStatuses.includes(
                                props.fund_request_status.value
                            )}
                            onClick={() => {
                                history.push(
                                    `/requests/request/submit/${props.id}`
                                );
                            }}
                        >
                            {renderIcon('edit_default')}
                        </Button>
                        {/* <Button
                            color='white'
                            className='p-1 m-0'
                            style={{ fontSize: '20px' }}
                            onClick={async () => {}}
                        >
                            {renderIcon('copy')}
                        </Button> */}
                        <Button
                            color='white'
                            className={`p-1 m-0 ${
                                props.fund_request_status.value === 'draft'
                                    ? 'text-danger'
                                    : 'opacity-25'
                            }`}
                            style={{ fontSize: '20px' }}
                            disabled={
                                props.fund_request_status.value !== 'draft'
                            }
                            onClick={() => {
                                setDeletingFundRequest(props);
                            }}
                        >
                            {renderIcon('delete')}
                        </Button>
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            <TableContainer
                showHeaders
                columns={columns}
                data={fundRequests}
                showPaginationComponents={true}
                showFilterComponents={false}
                NoDataCard={
                    <NoDataCard
                        title={`Welcome to the new Fund Request Portal!`}
                        body={
                            'Ready to begin? Click "New Request" to submit your first request online.'
                        }
                    />
                }
                isServerSidePagination={true}
                sort={true}
                page={currentPage}
                pageCount={totalPages}
                setPage={onPageChange}
                tableLoader={isLoading}
                setPageSize={setPageSize}
                pageSize={pageSize}
                onSortChange={onSort}
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
            />
        </div>
    );
}

export default ExpensesTable;
